import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export type ThemeMode = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

interface ThemeContextValue {
	theme: ThemeMode;
	resolvedTheme: ResolvedTheme;
	setTheme: (theme: ThemeMode) => void;
	toggleTheme: () => void;
	isDark: boolean;
}

const STORAGE_KEY = 'ielect-theme';

const defaultThemeContext: ThemeContextValue = {
	theme: 'light',
	resolvedTheme: 'light',
	setTheme: () => {},
	toggleTheme: () => {},
	isDark: false,
};

export const ThemeContext = createContext<ThemeContextValue>(defaultThemeContext);

const getSystemTheme = (): ResolvedTheme => {
	if (typeof window !== 'undefined') {
		return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
	}
	return 'light';
};

const getStoredTheme = (): ThemeMode => {
	if (typeof window !== 'undefined') {
		const stored = localStorage.getItem(STORAGE_KEY);
		if (stored === 'light' || stored === 'dark' || stored === 'system') {
			return stored;
		}
	}
	return 'light';
};

interface ThemeProviderProps {
	children: ReactNode;
	defaultTheme?: ThemeMode;
}

export const ThemeProvider = ({ children, defaultTheme = 'light' }: ThemeProviderProps) => {
	const [theme, setThemeState] = useState<ThemeMode>(() => {
		const stored = getStoredTheme();
		return stored || defaultTheme;
	});

	const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(getSystemTheme);

	// Listen for system theme changes
	useEffect(() => {
		const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

		const handleChange = (e: MediaQueryListEvent) => {
			setSystemTheme(e.matches ? 'dark' : 'light');
		};

		mediaQuery.addEventListener('change', handleChange);
		return () => mediaQuery.removeEventListener('change', handleChange);
	}, []);

	// Resolve the actual theme to use
	const resolvedTheme: ResolvedTheme = useMemo(() => {
		if (theme === 'system') {
			return systemTheme;
		}
		return theme;
	}, [theme, systemTheme]);

	// Apply theme to document
	useEffect(() => {
		const root = document.documentElement;

		// Set data-theme attribute
		root.setAttribute('data-theme', resolvedTheme);

		// Also set color-scheme for native elements
		root.style.colorScheme = resolvedTheme;

		// Update meta theme-color for mobile browsers
		const metaThemeColor = document.querySelector('meta[name="theme-color"]');
		if (metaThemeColor) {
			metaThemeColor.setAttribute('content', resolvedTheme === 'dark' ? '#1a1a1a' : '#ffffff');
		}
	}, [resolvedTheme]);

	// Persist theme to localStorage
	const setTheme = useCallback((newTheme: ThemeMode) => {
		setThemeState(newTheme);
		localStorage.setItem(STORAGE_KEY, newTheme);
	}, []);

	// Toggle between light and dark
	const toggleTheme = useCallback(() => {
		setTheme(resolvedTheme === 'dark' ? 'light' : 'dark');
	}, [resolvedTheme, setTheme]);

	const value = useMemo<ThemeContextValue>(
		() => ({
			theme,
			resolvedTheme,
			setTheme,
			toggleTheme,
			isDark: resolvedTheme === 'dark',
		}),
		[theme, resolvedTheme, setTheme, toggleTheme]
	);

	return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextValue => {
	const context = useContext(ThemeContext);

	if (!context) {
		throw new Error('useTheme must be used within a ThemeProvider');
	}

	return context;
};

// Hook for components that only need to check if dark mode
export const useIsDark = (): boolean => {
	const { isDark } = useTheme();
	return isDark;
};

export default ThemeProvider;
